import Loader from '../components/Loader';
import { getLogged, getLogout, loginUser, selectAuthError, selectAuthLoading, selectIsAuthenticated } from '../redux/main/authSlice';
import { useEffect, useState } from 'react';
import CryptoJS from "crypto-js";
import { useNavigate } from 'react-router-dom';
import { isTokenValid } from '../utils/isTokenValid';
import { getAccountDetails } from '../redux/main/accountSlice';
import { getUserDetails } from '../redux/main/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { apiService } from '../utils/apiService';

export default function Login() {
    // Controllo il token in localStorage
    // Se è ancora valido loggo l'utente manualmente coi dati salvati in 'installamelo-auth-user', senza rifare la chiamata per auth
    // ed eseguo le chiamate agli altri due thunk
    const token = localStorage.getItem('installamelo-auth-token');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    useEffect(() => {
        if (token) {
            console.log('Token trovato in localStorage, verifico validità...');
            const decodedToken = isTokenValid(token);
            if (decodedToken) {
                console.log('Token valido secondo la data di scadenza');
                const storedUser = localStorage.getItem('installamelo-auth-user');
                if (storedUser) {
                    try {
                        const user = JSON.parse(storedUser);
                        console.log('Dati utente trovati in localStorage:', user);
                        
                        // Imposto l'utente come loggato
                        dispatch(getLogged(user));
                        
                        try {
                            // Verifico la validità del token con una chiamata API
                            // Se il token non è valido, apiService gestirà l'errore 401
                            // reindirizzando l'utente alla pagina di login
                            console.log('Verifico la validità del token con una chiamata API...');
                            
                            // Utilizzo direttamente apiService invece di dispatch per avere un controllo più diretto
                            const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/accounts?accountId=${user?.accountId}`;
                            const headers = {
                                'Content-Type': 'application/json',
                                'auth-token': token
                            };
                            
                            // Effettuo la chiamata API per verificare il token
                            apiService.get(apiUrl, { headers })
                                .then(data => {
                                    console.log('Token valido, carico i dati utente e account');
                                    // Se la chiamata ha successo, carico i dati utente e account
                                    dispatch(getUserDetails({ accountId: user?.accountId, userId: user?.userId, token }));
                                    // Carico anche i dati dell'account
                                    dispatch(getAccountDetails({ accountId: user?.accountId, token }));
                                    // Stampo la lista dei moduli del menu ottenuta dal database
                                    console.log('Richiedo i dati dell\'account per il menu');
                                })
                                .catch(error => {
                                    console.error('Errore nel caricamento dei dati account:', error);
                                    // In caso di errore 401, apiService gestirà il reindirizzamento
                                    // In caso di altri errori, effettuo il logout
                                    if (error.message !== 'Sessione scaduta. Effettua nuovamente il login.') {
                                        localStorage.removeItem('installamelo-auth-user');
                                        localStorage.removeItem('installamelo-auth-token');
                                        dispatch(getLogout());
                                    }
                                });
                        } catch (error) {
                            console.error('Errore durante la verifica del token:', error);
                            localStorage.removeItem('installamelo-auth-user');
                            localStorage.removeItem('installamelo-auth-token');
                            dispatch(getLogout());
                        }
                    } catch (error) {
                        console.error('Errore nel parsing dei dati utente:', error);
                        localStorage.removeItem('installamelo-auth-user');
                        localStorage.removeItem('installamelo-auth-token');
                        dispatch(getLogout());
                    }
                } else {
                    console.error('Token valido, ma dati utente non presenti in localStorage');
                    localStorage.removeItem('installamelo-auth-token');
                    dispatch(getLogout());
                }
            } else {
                console.log('Token scaduto secondo la data di scadenza');
                localStorage.removeItem('installamelo-auth-user');
                localStorage.removeItem('installamelo-auth-token');
                dispatch(getLogout());
            }
        }
    }, [token, dispatch]);

    // Gestisco input utente
    const [inputData, setInputData] = useState({
        email: null,
        password: null
    });
    
    const handleUserInput = (e) => {
        const { id, value } = e.target;
        setInputData(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const isLoading = useSelector(selectAuthLoading);
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const error = useSelector(selectAuthError);

    const sendData = (e) => {
        e.preventDefault();
        const encryptedPassword = CryptoJS.AES.encrypt(
            inputData.password,
            import.meta.env.VITE_ENCKEY
        ).toString();
        if (inputData.email && inputData.password) {
            dispatch(loginUser({
                email: inputData.email,
                password: inputData.password
            }));
        }
    };
    
    // Il redirect dopo login è gestito da App.jsx che controlla authUser.customerId
    // e reindirizza automaticamente a /customerDashboard per i customer o / per gli employee

    return (
        <div className="w-full h-screen flex justify-center items-center">
            {/* Box-center */}
            <div className="box-center gap-4 !p-8">
                <img 
                    src="https://storage.googleapis.com/installamelo/65a14921ed1be038f6185b53/LOGO_CON%20SCRITTA%20nera_v02.png" 
                    alt="Installamelo Logo" 
                    className='h-12' 
                />
                <div>Inserisci le tue credenziali per accedere.</div>
                <form className='flex flex-col gap-4' onSubmit={sendData}>
                    {/* Email */}
                    <div className="input-box">
                        <label htmlFor="email">Email</label>
                        <input type="email" id="email" onChange={handleUserInput} />
                    </div>
                    {/* Password */}
                    <div className="input-box">
                        <label htmlFor="password">Password</label>
                        <input type="password" id="password" onChange={handleUserInput} />
                    </div>
                    <div className='w-full flex justify-between'>
                        {/* Remember */}
                        <div className='check-box'>
                            <input type="checkbox" />
                            <label htmlFor="remember">Ricordami</label>
                        </div>
                        {/* Forgot password */}
                        <div>Password dimenticata?</div>
                    </div>
                    {/* Messaggio di errore */}
                    {error && (
                        <div className="text-red-500 text-sm font-medium">
                            Email o password sbagliati
                        </div>
                    )}
                    
                    {/* Button */}
                    {!isLoading && !isAuthenticated && inputData.email && inputData.password && <button type="submit" className='primary'>Entra</button>}
                    {!isLoading && !isAuthenticated && (!inputData.email || !inputData.password) && <button className='disabled'>Entra</button>}

                    {/* Loader */}
                    {isLoading && <Loader />}
                </form>
                
                {/* Link registrazione */}
                <div className='text-center mt-4 pt-4 border-t border-gray-200'>
                    <p className='text-sm text-gray-600 mb-2'>Non hai un account?</p>
                    <a 
                        href='/register' 
                        className='text-sm font-medium hover:underline'
                        style={{ color: '#383838' }}
                    >
                        Registrati ora
                    </a>
                </div>
            </div>
        </div>
    );
}
